import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Home, ShoppingBag, Star, Settings, LogOut, Menu, X, User, Wallet, ScanLine, BarChart2, Eye, ChevronDown, UserCircle, LifeBuoy, Compass, Shield, Plus, Search } from 'lucide-react';
import MarakinFooter from './MarakinFooter';
import { useSystemBarsStyle } from '../../native/systemBars';
import './Navbar.css';
// B-44.1 Tours & festivals — one menu item, rugby club staff only.
import { Bus } from 'lucide-react';
import { canSeeTours } from '../../lib/tourListing';
import ClubSwitcher, { switcherClubs } from './ClubSwitcher';

/**
 * Unified Navbar used across club pages, sponsor portal, and standalone pages.
 *
 * Props:
 *  - club        — club object (for club pages)
 *  - portalName  — string like "Sponsor Portal" (for non-club pages)
 *  - portalHome  — link target for portalName (e.g. "/sponsor")
 *  - links       — override nav links array [{ to, label, icon }]
 *  - rightContent — override right-side content (JSX)
 */
// B-58 slice 3: "3 waiting to join" on the Admin door (9+ beyond nine).
const NavBadge = ({ n }) => (n > 0
  ? <span className="navbar-link-badge"><span aria-hidden="true">{n > 9 ? '9+' : n}</span><span className="navbar-sr">{` (${n} waiting to join)`}</span></span>
  : null);

const Navbar = ({ club, portalName, portalHome, links: overrideLinks, rightContent }) => {
  // Green bar under the status bar → light status-bar icons (native only).
  useSystemBarsStyle('DARK');
  const { clubSlug } = useParams();
  const { user, loading: authLoading, logout, getMembership, impersonating, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // WT-14 QA: Login / Join bring people back to this page (not from the auth pages themselves).
  const authFrom = /^\/(login|register)\b/.test(location.pathname)
    ? undefined : { from: `${location.pathname}${location.search}` };
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const hamburgerRef = useRef(null);
  // W-2: the guided tour asks for the ☰ menu when a step's anchor lives
  // inside it (phones) — e.g. "Club Admin" before hopping into it.
  useEffect(() => {
    const onOpen = () => setMenuOpen(true);
    window.addEventListener('marakin:open-menu', onOpen);
    return () => window.removeEventListener('marakin:open-menu', onOpen);
  }, []);
  const membership = clubSlug ? getMembership(clubSlug) : null;

  // SB-7: remember the last club viewed on this device so the club-less
  // /wallet shortcut can fall back to it (WalletRedirect). `club` is only
  // passed once the club has loaded, so a mistyped slug is never saved.
  useEffect(() => {
    if (!club || !clubSlug) return;
    try { localStorage.setItem('marakin_last_club', clubSlug); } catch { /* storage unavailable */ }
  }, [club, clubSlug]);

  const handleLogout = () => { logout(); navigate('/'); };

  // Close the account dropdown on outside-click / Escape — standard
  // popover behaviour. Mobile uses the hamburger sheet instead.
  useEffect(() => {
    if (!accountOpen) return;
    const onClick = e => {
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    const onKey = e => { if (e.key === 'Escape') setAccountOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [accountOpen]);

  // Close the hamburger menu on any tap outside it (UI tweak #4) — not just
  // the X. touchstart included so the first tap on mobile closes it rather
  // than needing a full click. Taps on the hamburger button itself are
  // ignored here so the button's own toggle doesn't immediately re-open it.
  useEffect(() => {
    if (!menuOpen) return;
    const onPress = e => {
      if (mobileMenuRef.current && mobileMenuRef.current.contains(e.target)) return;
      if (hamburgerRef.current && hamburgerRef.current.contains(e.target)) return;
      setMenuOpen(false);
    };
    const onKey = e => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onPress);
    document.addEventListener('touchstart', onPress);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPress);
      document.removeEventListener('touchstart', onPress);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // B-83 (Stuart): My Clubs already offers both, so the menu doesn't repeat
  // them there; "Start a new club" is also pointless on the wizard itself.
  const onMyClubs = location.pathname === '/my-clubs';
  const multiClub = (user?.memberships || []).length > 1 && !onMyClubs;
  // QA: sponsor-only accounts (every membership a sponsor login) don't start clubs.
  const sponsorOnly = (user?.memberships || []).length > 0 && user.memberships.every(m => m.role === 'sponsor');

  // Build nav links — use override if provided, else build from club context + role
  const isSponsor = membership?.role === 'sponsor';
  const isAdmin = membership?.role === 'admin';
  const showStartClub = !isSponsor && !sponsorOnly && !impersonating
    && !onMyClubs && location.pathname !== '/create-club';
  // B-58: "Join another club" → /find-club, on the same terms (single-club
  // people never see My Clubs, where the link also lives).
  const showFindClub = !isSponsor && !sponsorOnly && !impersonating
    && !onMyClubs && location.pathname !== '/find-club';
  const findClubLabel = (user?.memberships || []).length ? 'Join another club' : 'Find your club';
  // Stuart, 30 Oct: the club name changes club when there's another of your
  // own clubs to go to (ClubSwitcher); otherwise it's the link to the hub.
  const canSwitchClub = !!user && !!clubSlug
    && switcherClubs(user.memberships).some(m => m.clubSlug !== clubSlug);
  // B-11 follow-through (slice 2): team managers have had AdminPage access
  // (scoped to Members + Reports) since 27 Aug, but no visible way in — the
  // navbar only showed Club Admin to admins. Detect the TM hat too so the
  // door is actually on the wall (and so the TM guided tour can point at it).
  const isTeamManager = !!membership && (
    membership.role === 'team_manager'
    || (Array.isArray(membership.teamRoles) && membership.teamRoles.some(tr => tr.role === 'team_manager'))
  );

  // B-44.1: "Tours & festivals" sits in the menus (account dropdown on
  // desktop, ☰ on phones), not on the bar — rugby admins/TMs/coaches only.
  const showTours = !!clubSlug && !overrideLinks && canSeeTours(club?.sport || membership?.sport, membership);

  // B-58 slice 3: a count badge on the Admin door while join requests wait.
  // Club admins only (they approve); a count, never names.
  const { data: joinWaiting = 0 } = useQuery({
    queryKey: ['join-requests-count', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/join-requests/count`).then(r => Number(r.data?.waiting) || 0),
    enabled: !!user && !!clubSlug && isAdmin && !overrideLinks,
    staleTime: 60000,
    retry: false,
  });

  const navLinks = overrideLinks || (clubSlug ? (
    isSponsor
      ? [
          // Sponsors see only scan + attribution + their settings — no hub, kit shop, offers, wallet
          { to: `/${clubSlug}/scan`, label: 'Scan QR', icon: ScanLine, tour: 'nav-scan' },
          { to: `/${clubSlug}/attribution`, label: 'Attribution', icon: BarChart2, tour: 'nav-attribution' },
          { to: `/${clubSlug}/sponsor-settings`, label: 'Settings', icon: Settings },
        ]
      : [
          // Order (Stuart, 16 Sept): Club Hub → Wallet → Kit Shop, then the
          // role-specific door (Admin).
          { to: `/${clubSlug}`, label: 'Club Hub', icon: Home },
          // v35 (Stuart, 16 Sept): ONE place for offers + codes + the club card.
          // SB-7 (19 Sept): Wallet for EVERYONE signed in, staff and non-members
          // included (non-members: an empty card slot + the offers; B-66 —
          // Marakin staff: their real wallet, or a labelled staff preview);
          // Sponsor Offers stays the shop window for signed-out visitors.
          ...(user
            ? [{ to: `/${clubSlug}/wallet`, label: 'Wallet', icon: Wallet, tour: 'nav-wallet' }]
            : [{ to: `/${clubSlug}/sponsors`, label: 'Sponsor Offers', icon: Star, tour: 'nav-offers' }]),
          { to: `/${clubSlug}/shop`, label: 'Shop', icon: ShoppingBag, tour: 'nav-shop' },
          // B-46 (Stuart, 7 Oct — declutter): the card scanner now opens from
          // the Wallet (club staff only, lib/membershipAccess) and the admins'
          // sponsorship dashboard from Admin → Reports → Sponsorship.
          // "Club Admin" is just "Admin" on the bar.
          ...((isAdmin || isTeamManager) ? [{ to: `/${clubSlug}/admin`, label: 'Admin', icon: Settings, tour: 'nav-admin', badge: isAdmin ? joinWaiting : 0 }] : []),
        ]
  ) : []);

  // B-46: pages that lost their own bar item light up the door they open from —
  // the scanner under Wallet, the sponsorship dashboard under Admin (not for sponsors,
  // for whom /attribution is its own item).
  const isActiveLink = (to) => location.pathname === to
    || (!!clubSlug && !isSponsor && (
      (to === `/${clubSlug}/wallet` && location.pathname === `/${clubSlug}/scan-cards`)
      || (to === `/${clubSlug}/admin` && location.pathname === `/${clubSlug}/attribution`)));

  // Relaunch the guided tour for this club (slice 2). The tour runs on the
  // role's home surface — /scan for sponsors, the hub for everyone else. If
  // we're already there, fire the event; otherwise flag + navigate and the
  // GuidedTour on the destination picks the flag up.
  const startTour = () => {
    setAccountOpen(false);
    setMenuOpen(false);
    const dest = isSponsor ? `/${clubSlug}/scan` : `/${clubSlug}`;
    if (location.pathname === dest) {
      window.dispatchEvent(new CustomEvent('marakin:start-tour'));
    } else {
      try { sessionStorage.setItem('marakin_tour_relaunch', clubSlug); } catch { /* storage unavailable */ }
      navigate(dest);
    }
  };

  const accentColor = club?.accent_color || 'var(--marakin-accent)';

  return (
    <>
    {impersonating && (
      <div style={{
        background: '#C4943A',
        color: '#2C2B28',
        padding: '8px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        fontSize: '0.85rem',
        fontWeight: 600,
        position: 'sticky',
        top: 0,
        paddingTop: 'calc(8px + var(--sat))', /* B-15: this bar sits above the navbar, under the status bar */
        zIndex: 1100
      }}>
        <Eye size={15} />
        Viewing as {impersonating.firstName} {impersonating.lastName} ({impersonating.email})
        <button
          onClick={async () => {
            const returnUrl = localStorage.getItem('marakin_impersonation_return_url') || '/marakin-admin/clubs';
            localStorage.removeItem('marakin_impersonation_return_url');
            // B-71: false = no staff token to go back to, so it signed out.
            const restored = await exitImpersonation();
            // Full reload so PlatformAuthContext re-initialises with the restored platform token
            window.location.href = restored ? returnUrl : '/marakin-admin/login';
          }}
          style={{
            marginLeft: '12px',
            padding: '3px 12px',
            background: '#2C2B28',
            color: '#F4EFE6',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.8rem',
            fontWeight: 600
          }}
        >
          Exit
        </button>
      </div>
    )}
    <nav className="navbar" style={{ '--accent': accentColor }}>
      <div className="navbar-inner">
        {/* Left: Marakin logo + context brand + nav links */}
        <div className="navbar-left">
          <Link to="/" className="navbar-marakin-brand">
            <img src="/marakin-logo-cream.png" alt="Marakin" className="navbar-marakin-logo" />
          </Link>

          {/* Club brand */}
          {club && (
            <>
              <div className="navbar-divider" />
              {canSwitchClub ? (
                <ClubSwitcher club={club} clubSlug={clubSlug} memberships={user.memberships} userId={user.id}
                  onOpen={() => { setAccountOpen(false); setMenuOpen(false); }}>
                  {club.logo_url && <img src={club.logo_url} alt="" className="navbar-club-logo" />}
                  <span className="navbar-brand-text">
                    <span className="navbar-club-name">{club.name}</span>
                    {club.sport && <span className="navbar-sport">{club.sport}</span>}
                  </span>
                </ClubSwitcher>
              ) : (
                <Link to={`/${clubSlug}`} className="navbar-club-brand">
                  {club.logo_url && <img src={club.logo_url} alt={club.name} className="navbar-club-logo" />}
                  <div className="navbar-brand-text">
                    <span className="navbar-club-name">{club.name}</span>
                    {club.sport && <span className="navbar-sport">{club.sport}</span>}
                  </div>
                </Link>
              )}
            </>
          )}

          {/* Portal brand (sponsor portal, etc.) */}
          {!club && portalName && (
            <>
              <div className="navbar-divider" />
              <Link to={portalHome || '/'} className="navbar-club-brand">
                <div className="navbar-brand-text">
                  <span className="navbar-club-name">{portalName}</span>
                </div>
              </Link>
            </>
          )}

          <div className="navbar-links">
            {navLinks.map(({ to, label, icon: Icon, tour, badge }) => (
              <Link key={to} to={to} className={`navbar-link ${isActiveLink(to) ? 'active' : ''}`}
                {...(tour ? { 'data-tour': tour } : {})}>
                <Icon size={16} />
                {label}
                <NavBadge n={badge} />
              </Link>
            ))}
          </div>
        </div>

        {/* Right: user actions — can be overridden */}
        <div className="navbar-actions">
          {rightContent || (
            <>
              {user ? (
                <div className="navbar-account" ref={accountRef}>
                  <button
                    type="button"
                    className="navbar-account-trigger"
                    onClick={() => setAccountOpen(o => !o)}
                    aria-haspopup="menu"
                    aria-expanded={accountOpen}
                  >
                    <UserCircle size={18} />
                    <span className="navbar-account-name">{user.firstName}</span>
                    <ChevronDown size={14} className={`navbar-account-chev ${accountOpen ? 'open' : ''}`} />
                  </button>
                  {accountOpen && (
                    <div className="navbar-account-menu" role="menu">
                      <div className="navbar-account-header">
                        <div className="navbar-account-header-name">{user.firstName} {user.lastName}</div>
                        {user.email && <div className="navbar-account-header-email">{user.email}</div>}
                      </div>
                      {showTours && (
                        <Link
                          to={`/${clubSlug}/tours`}
                          className="navbar-account-item"
                          role="menuitem"
                          onClick={() => setAccountOpen(false)}
                        >
                          <Bus size={15} /> Tours &amp; festivals
                        </Link>
                      )}
                      {clubSlug && !isSponsor && (
                        <Link
                          to={`/${clubSlug}/account`}
                          className="navbar-account-item"
                          role="menuitem"
                          onClick={() => setAccountOpen(false)}
                        >
                          <User size={15} /> My Account
                          <span style={{
                            marginLeft: 'auto',
                            fontSize: 12,
                            color: 'rgba(244,239,230,0.45)',
                            fontWeight: 500,
                          }}>
                            Profile · Family
                          </span>
                        </Link>
                      )}
                      {multiClub && (
                        <Link
                          to="/my-clubs"
                          className="navbar-account-item"
                          role="menuitem"
                          onClick={() => setAccountOpen(false)}
                        >
                          <Home size={15} /> Switch club
                        </Link>
                      )}
                      {showFindClub && (
                        <Link
                          to="/find-club"
                          className="navbar-account-item"
                          role="menuitem"
                          onClick={() => setAccountOpen(false)}
                        >
                          <Search size={15} /> {findClubLabel}
                        </Link>
                      )}
                      {/* B-83: start another club from anywhere (single-club
                          people never see My Clubs). Not sponsors, not view-as. */}
                      {showStartClub && (
                        <Link
                          to="/create-club"
                          className="navbar-account-item"
                          role="menuitem"
                          onClick={() => setAccountOpen(false)}
                        >
                          <Plus size={15} color="var(--gold, #C4943A)" /> Start a new club
                        </Link>
                      )}
                      {clubSlug && membership && (
                        <button
                          className="navbar-account-item"
                          role="menuitem"
                          onClick={startTour}
                        >
                          <Compass size={15} /> Take the tour
                        </button>
                      )}
                      <Link
                        to={clubSlug ? `/${clubSlug}/report-problem` : '/report-problem'}
                        className="navbar-account-item"
                        role="menuitem"
                        onClick={() => setAccountOpen(false)}
                      >
                        <LifeBuoy size={15} /> Report a problem
                      </Link>
                      {/* SB-8: way into the staff portal for Marakin staff logins
                          (the portal keeps its own sign-in). */}
                      {user.isPlatformUser && (
                        <Link
                          to="/marakin-admin"
                          className="navbar-account-item"
                          role="menuitem"
                          onClick={() => setAccountOpen(false)}
                        >
                          <Shield size={15} /> Marakin staff
                        </Link>
                      )}
                      <button
                        className="navbar-account-item navbar-account-item-danger"
                        role="menuitem"
                        onClick={() => { setAccountOpen(false); handleLogout(); }}
                      >
                        <LogOut size={15} /> Log out
                      </button>
                    </div>
                  )}
                </div>
              ) : authLoading ? null : ( /* WT-1: no Login / Join while a stored session loads or waits on signal */
                <div className="navbar-auth">
                                    <Link to="/login" state={authFrom} className="btn btn-ghost">Login</Link>
                  <Link to="/register" state={authFrom} className="btn btn-primary">Join</Link>
                </div>
              )}
            </>
          )}
          <button className="navbar-hamburger" ref={hamburgerRef} data-tour="nav-menu" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="navbar-mobile-menu" ref={mobileMenuRef}>
          {/* SB-8: signed-in name + Log out at the top — at the foot of a long
              menu it went unnoticed on phones. */}
          {user && (
            <div className="navbar-mobile-account">
              <span className="navbar-mobile-account-name">{user.firstName}</span>
              <button className="navbar-mobile-logout" onClick={() => { setMenuOpen(false); handleLogout(); }}>
                <LogOut size={16} /> Log out
              </button>
            </div>
          )}
          {navLinks.map(({ to, label, icon: Icon, tour, badge }) => (
            <Link key={to} to={to} className="navbar-mobile-link" onClick={() => setMenuOpen(false)}
              {...(tour ? { 'data-tour': tour } : {})}>
              <Icon size={18} /> {label}
              <NavBadge n={badge} />
            </Link>
          ))}
          {showTours && (
            <Link to={`/${clubSlug}/tours`} className="navbar-mobile-link" onClick={() => setMenuOpen(false)}>
              <Bus size={18} /> Tours &amp; festivals
            </Link>
          )}
          {user && clubSlug && membership && (
            <button className="navbar-mobile-link" onClick={startTour}>
              <Compass size={18} /> Take the tour
            </button>
          )}
          {user && clubSlug && !isSponsor && (
            <Link to={`/${clubSlug}/account`} className="navbar-mobile-link" onClick={() => setMenuOpen(false)}>
              <User size={18} /> My Account
            </Link>
          )}
          {user && multiClub && (
            <Link to="/my-clubs" className="navbar-mobile-link" onClick={() => setMenuOpen(false)}>
              <Home size={18} /> Switch club
            </Link>
          )}
          {user && showFindClub && (
            <Link to="/find-club" className="navbar-mobile-link" onClick={() => setMenuOpen(false)}>
              <Search size={18} /> {findClubLabel}
            </Link>
          )}
          {user && showStartClub && (
            <Link to="/create-club" className="navbar-mobile-link" onClick={() => setMenuOpen(false)}>
              <Plus size={18} color="var(--gold, #C4943A)" /> Start a new club
            </Link>
          )}
          {user && (
            <Link to={clubSlug ? `/${clubSlug}/report-problem` : '/report-problem'} className="navbar-mobile-link" onClick={() => setMenuOpen(false)}>
              <LifeBuoy size={18} /> Report a problem
            </Link>
          )}
          {user?.isPlatformUser && (
            <Link to="/marakin-admin" className="navbar-mobile-link" onClick={() => setMenuOpen(false)}>
              <Shield size={18} /> Marakin staff
            </Link>
          )}
          {!user && !authLoading && (
              <>
                <Link to="/login" state={authFrom} className="navbar-mobile-link" onClick={() => setMenuOpen(false)}><User size={18} /> Login</Link>
                <Link to="/register" state={authFrom} className="navbar-mobile-link" onClick={() => setMenuOpen(false)}><User size={18} /> Register</Link>
              </>
          )}
        </div>
      )}
    </nav>
    {/* Fixed "Powered by Marakin" footer — ships with the navbar so every
        club-facing screen gets it without per-page wiring (UI tweak #2). */}
    <MarakinFooter />
    </>
  );
};

export default Navbar;
